import React, {
    createContext, useContext, useMemo, useState,
} from 'react';
import { downloadFileFromUrl } from 'helpers/file/download';
import { Deal } from 'views/Deals/types';
import { useParams } from 'react-router-dom';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { IOptionNumberValue } from 'bricks/types';
import { SimplifiedScenario } from 'components/DealScenarioDropdown/types';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import useAuthContext from 'contexts/AuthContext';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { isEmpty } from 'lodash';
import { highlightFields } from '../helpers/highlight';
import { highlightCriteria } from '../constants';
import { getComparisonPdf } from '../hooks/useGetComparisonPdf';
import { useGetBOEScenarios } from '../hooks';
import { DealPageLocalStorage } from '../types';

interface ScenarioContextType {
    scenarios: SimplifiedScenario[];
    selectedScenarios: IOptionNumberValue[];
    addScenario: (scenario: SimplifiedScenario) => void;
    removeScenario: (id: number) => void;
    switchScenario: (currentId: number, newScenario: SimplifiedScenario) => void;
    getAvailableOptions: () => IOptionNumberValue[];
    initializeData: (scenarios: SimplifiedScenario[], options: IOptionNumberValue[]) => void;
    handlePdfExport: (deal: Deal, selectedScenarios: IOptionNumberValue[]) => void;
    selectedScenariosData: BOEScenario[];
}

interface IScenarioProvider {
    children: React.ReactNode;
}

const ScenarioContext = createContext<ScenarioContextType | undefined>(undefined);

export const ScenarioProvider: React.FC<IScenarioProvider> = ({ children }: IScenarioProvider) => {
    const { dealId } = useParams<{ dealId: string }>();
    const { selectedOrganizationId } = useAuthContext();
    const [dealPageLocalStorage, setDealPageLocalStorage] = useLocalStorage<DealPageLocalStorage>(PageSpecificKeys.ScenarioComparisonPage, {});

    const [scenarios, setScenarios] = useState<SimplifiedScenario[]>([]);
    const [selectedScenarios, setSelectedScenarios] = useState<IOptionNumberValue[]>([]);

    const { data: deal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const { data: BOEScenarios } = useGetBOEScenarios(selectedOrganizationId!, deal?.id);

    const updateScenarioComparisonLocalStorage = (scenarioIds: number[]) => {
        const updatedDealStorage = {
            ...dealPageLocalStorage,
            [Number(dealId)]: scenarioIds,
        };
        setDealPageLocalStorage(updatedDealStorage);
    };
    const addScenario = (scenario: SimplifiedScenario) => {
        setSelectedScenarios(prev => {
            const updatedScenarios = [...prev, { label: scenario.name, value: scenario.id }];
            updateScenarioComparisonLocalStorage(updatedScenarios.map(s => s.value));
            return updatedScenarios;
        });
    };

    const removeScenario = (id: number) => {
        setSelectedScenarios(prev => {
            const updatedScenarios = prev.filter(option => option.value !== id);
            updateScenarioComparisonLocalStorage(updatedScenarios.map(s => s.value));
            return updatedScenarios;
        });
    };

    const switchScenario = (currentId: number, newScenario: SimplifiedScenario) => {
        setSelectedScenarios(prev => {
            const updatedScenarios = prev.map(option => (option.value === currentId ? { label: newScenario.name, value: newScenario.id } : option));
            updateScenarioComparisonLocalStorage(updatedScenarios.map(s => s.value));
            return updatedScenarios;
        });
    };

    const getAvailableOptions = (): IOptionNumberValue[] => {
        const selectedIds = selectedScenarios.map(option => option.value);
        return scenarios
            .filter(scenario => !selectedIds.includes(scenario.id))
            .map(scenario => ({
                label: scenario.name,
                value: scenario.id,
            }));
    };

    const selectedScenariosData = useMemo(() => {
        const selectedIds = selectedScenarios.map(option => +option.value);
        const filteredScenarios = (BOEScenarios?.filter(scenario => selectedIds.includes(scenario.id)) || [])
            .sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id));
        return highlightFields(filteredScenarios, highlightCriteria);
    }, [selectedScenarios, BOEScenarios]);

    const initializeData = (initialScenarios: SimplifiedScenario[], initialOptions: IOptionNumberValue[]) => {
        setScenarios(initialScenarios);

        if (isEmpty(dealPageLocalStorage)) {
            updateScenarioComparisonLocalStorage(initialOptions.map(option => option.value));
            setSelectedScenarios(initialOptions);
        } else {
            const storedScenarioIds = dealPageLocalStorage[Number(dealId)] || [];
            const validStoredScenarios = initialScenarios.filter(scenario => storedScenarioIds.includes(scenario.id));
            const storedOptions = validStoredScenarios.map(scenario => ({ label: scenario.name, value: scenario.id }));
            setSelectedScenarios(storedOptions);
        }
    };

    const handlePdfExport = async (deal: Deal, selectedScenarios: IOptionNumberValue[]) => {
        const selectedIds = selectedScenarios.map(option => +option.value);
        if (deal) {
            try {
                const response = await getComparisonPdf(deal.organizationId, deal.id, selectedIds);
                const pdfUrl = response.url;
                if (pdfUrl) {
                    await downloadFileFromUrl(pdfUrl, `${deal.dealName}-scenario-comparison`, 'pdf', true);
                } else {
                    console.error('Failed to export report PDF: No URL returned');
                }
            } catch (error) {
                console.error('Failed to export report PDF:', error);
            }
        }
    };

    return (
        <ScenarioContext.Provider
            value={{
                scenarios,
                selectedScenarios,
                addScenario,
                removeScenario,
                switchScenario,
                getAvailableOptions,
                initializeData,
                handlePdfExport,
                selectedScenariosData,
            }}
        >
            {children}
        </ScenarioContext.Provider>
    );
};

export const useScenarioContext = (): ScenarioContextType => {
    const context = useContext(ScenarioContext);
    if (!context) {
        throw new Error('useScenarioContext must be used within a ScenarioProvider');
    }
    return context;
};
